'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import Image from 'next/image';

import Navbar from './Navbar';
import Footer from './footer/Footer';
import FeaturedCourses from './landing/FeaturedCourses';
import WhyQuattron from './landing/WhyQuattron';
import LottieSlideshow from './LottieSlideshow';
import LandingFAQ from './landing/LandingFAQ';

import styles from '../styles/LandingPage.module.css';
import SocialProof from "./Proof/SocialProof";

const CYCLING_WORDS = ['AI Researchers', 'Coders', 'Creators', 'Innovators'];

const CATEGORIES = [
  { name: 'Classes for kids',            icon: '⭐', filterLabel: 'All', alt: 'Star icon representing all Classes for kids'},
  { name: 'Minecraft Coding',      icon: '/images/minecraft-icon.png',    filterLabel: 'Minecraft', alt: 'Minecraft coding'},
  { name: 'Roblox Coding',          icon: '/images/roblox-icon.png',       filterLabel: 'Roblox', alt: 'Roblox Coding'},
  { name: 'AI for kids', icon: '/images/ai-icon.png',           filterLabel: 'AI', alt: 'AI for kids'},
  { name: 'Python for kids',                icon: '/images/python-icon.png',       filterLabel: 'Python', alt: 'Python for kids'},
  { name: 'Scratch class',               icon: '/images/scratch-icon.png',      filterLabel: 'Scratch', alt: 'Scratch class'},
];

function useWindowWidth() {
  const [width, setWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return width;
}

export default function LandingPage({ user }) {
  const [wordIndex, setWordIndex] = useState(0);
  const router     = useRouter();
  const windowWidth = useWindowWidth();

  // Pick slideshow size based on viewport so Lottie always renders correctly
  const slideshowSize =
    windowWidth <= 480  ? 200 :
    windowWidth <= 640  ? 230 :
    windowWidth <= 900  ? 280 : 260;

  useEffect(() => {
    const interval = setInterval(
      () => setWordIndex((prev) => (prev + 1) % CYCLING_WORDS.length),
      2500
    );
    return () => clearInterval(interval);
  }, []);

  const ctaHref  = user ? '/catalog' : '/register';
  const ctaLabel = user ? 'Browse Coding Classes' : 'Start with a Free Class';

  const handleCategoryClick = (filterLabel) => {
    try {
      if (typeof window !== 'undefined') {
        const mappedFilter = filterLabel === 'All' ? 'All' : 'Coding & Technology';
        sessionStorage.setItem('catalogFilter', mappedFilter);
      }
    } catch (e) {
      console.warn('Could not access sessionStorage', e);
    }
    router.push('/catalog');
  };

  return (
    <>
      <Navbar />

      {/* ===== NOTICE BAR ===== */}
      <div className={styles.noticeBar} role="region" aria-label="Updates">
        <div className={styles.noticeInner}>
          <p className={styles.noticeText}>
            <Link href="/footer/esa" className={styles.noticeLink}>
              💰 Use Your ESA Funds for Classes at PenguinLearn - Learn More
            </Link>
          </p>
        </div>
      </div>

      <main className={styles.container}>
        {/* ===== HERO ===== */}
        <section className={styles.hero}>
          {/* Penguin doodles */}
          <div
            className={`${styles.penguinDrawing} ${styles.penguinHeroTopLeft}`}
            aria-hidden="true"
          >
            <div className={styles.penguinFace}>
              <span className={styles.penguinEye}></span>
              <span className={styles.penguinEye}></span>
              <span className={styles.penguinBeak}></span>
            </div>
            <div className={styles.penguinFeet}>
              <span></span>
              <span></span>
            </div>
          </div>

          <div
            className={`${styles.penguinDrawingSmall} ${styles.penguinHeroBottomRight}`}
            aria-hidden="true"
          >
            <div className={styles.penguinFace}>
              <span className={styles.penguinEye}></span>
              <span className={styles.penguinEye}></span>
              <span className={styles.penguinBeak}></span>
            </div>
            <div className={styles.penguinFeet}>
              <span></span>
              <span></span>
            </div>
          </div>

          {/* ── Hero copy ── */}
          <div className={styles.heroText}>
            <h1 className={styles.h2}>
              Coding Classes to Raise the Next Generation of{' '}
              <span className={styles.cycleWord}>
                {CYCLING_WORDS[wordIndex]}
              </span>
            </h1>
            <span className={styles.limited}>
              Free First Class · No Credit Card Required
            </span>
            <Link href={ctaHref} className={styles.cta} prefetch>
              {ctaLabel}
            </Link>
          </div>
          {/* Right hero media — always rendered, size driven by JS */}
          <div className={styles.heroMedia}>
            <div className={styles.slideshowWrapper}>
              <LottieSlideshow size={slideshowSize} />
            </div>
          </div>
          {/* Floating penguin mascot */}
          <div className={styles.heroPenguinFloating}>
            <Image
              src="/images/penguin.png"
              alt="Penguin mascot"
              width={220}
              height={220}
              className={styles.heroPenguinImage}
            />
          </div>
        </section>

        <SocialProof />

        {/* ===== CODING TRACK SPOTLIGHT ===== */}
        <section className={styles.tracksSection}>
          <h2 className={styles.sectionTitle}>Pick Your Coding Adventure</h2>
          <p className={styles.sectionSubtitle}>
            Every coding for kids class is live, interactive, and taught by a vetted educator. 
            From Python for kids to Roblox coding and AI for kids - choose the world your child wants to build in.
        </p>

          <div className={styles.categoryGrid}>
            {CATEGORIES.map((cat) => (
              <button
                key={cat.filterLabel}
                type="button"
                className={styles.categoryCard}
                onClick={() => handleCategoryClick(cat.filterLabel)}
              >
                <div className={styles.categoryIcon}>
                  {cat.icon.startsWith('/') ? (
                    <Image
                      src={cat.icon}
                      alt={cat.name}
                      width={48}
                      height={48}
                      style={{ objectFit: 'contain' }}
                    />
                  ) : (
                    <span>{cat.icon}</span>
                  )}
                </div>
                <span className={styles.categoryName}>{cat.name}</span>
              </button>
            ))}
          </div>
        </section>
        {/* ===== ESA / CLASSWALLET TRUST SECTION ===== */}
        <section className={styles.esaTrustSection}>
          <div className={styles.esaTrustBox}>
            <img
              src="/images/classwallet-logo-blue-horiz-transp-rgb.png"
              alt="ClassWallet logo"
              className={styles.classWalletLogo}
            />
            <p className={styles.esaText}>
              <strong>Arizona, Alabama, and Arkansas families</strong> can use
              their ESA funds for classes on PenguinLearn through
              ClassWallet
            </p>
            <Link href="/footer/esa" className={styles.esaButton}>
              Learn More
            </Link>
          </div>
        </section>

        <section className={styles.videoSection}>
          <div className={styles.videoWrapper}>
            <iframe
              width="560"
              height="315"
              src="https://www.youtube.com/embed/qdrf_1uz7jI?si=HRR7gzC5QOeCq6DX"
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          </div>
        </section>

        {/* ===== FEATURED COURSES ===== */}
        <div className={styles.featuredSection}>
          <FeaturedCourses />
        </div> 
        {/* Desktop/tablet penguin next to FAQ */}
        <div className={styles.heroPenguinFloatingRight} aria-hidden="true">
          <Image
            src="/images/penguin2.png"
            alt="PenguinLearn mascot"
            width={220}
            height={220}
            className={styles.heroPenguinImage}
          />
        </div>
        {/* ===== FAQ ===== */}
        <div className={styles.faqSection}>
          <LandingFAQ />
        </div>
      </main>

      <Footer />
    </>
  );
}